import {
  Card,
  CardBody,
  Container,
  CardHeader,
  Stack,
  IconButton,
  Heading, Button,
  useDisclosure,
  Modal,
  FormControl,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  CardFooter,
  useToast,
  Flex,
  Spacer
} from "@chakra-ui/react";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import CustomTable from "components/table/CustomTable";
import { ChevronLeftIcon, ChevronRightIcon, DeleteIcon, DragHandleIcon, EditIcon, SmallAddIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { truncateString, updateIndexString } from "utils/utils";
import { useAppDispatch } from "store/store.hook";
import { Link, useParams } from "react-router-dom";
import { TemplateModalMode } from "types/TemplateModalMode";
import { deleteDocumentAction, patchDocumentAction, postDocumentAction, updateActionsReindex } from "service/api/document.api";
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import ContainerDnd from "components/dragndrop/ContainerDnd";
import { addInEditingDocumentAction, deleteInEditingDocumentAction, updateInEditingDocumentAction, updateInEditingDocumentActions } from "store/reducers/documents.slice";
import { Breadcrumb } from "types/Breadcrumb";

type TableData = {
  id: number;
  title: string;
  subtitle: string;
  index: string;
};
const columnHelper = createColumnHelper<TableData>();

type DocumentPageProps = {
  isUserAdmin: boolean;
  isUserAdminRolesEditor: boolean;
};


/**
 * Component for managing document page actions.
 * 
 * @param {DocumentPageProps} props - The properties for the component.
 * @param {boolean} props.isUserAdmin - Indicates if the user is an admin.
 * @param {boolean} props.isUserAdminRolesEditor - Indicates if the user has roles editor permissions.
 * 
 * @returns {JSX.Element} The rendered component.
 * 
 * @remarks
 * This component handles the display and management of actions related to a document page.
 * It includes functionality for adding, editing, deleting, and reordering actions.
 * 
 * @example
 * ```tsx
 * <DocumentPageActions isUserAdmin={true} isUserAdminRolesEditor={false} />
 * ```
 */
export default function DocumentPageActions({ isUserAdmin, isUserAdminRolesEditor }: DocumentPageProps) {

  const params = useParams();
  const dispatch = useAppDispatch();

  const inEditingDocumentId = useSelector((state: any) => state.documents.inEditingDocumentId);
  const targetId = useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id == inEditingDocumentId).targets.find((target: any) => target.index == params.targetIndex)._id);
  const subtargetId = useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id == inEditingDocumentId).targets.find((target: any) => target.index == params.targetIndex).subtargets.find((subtarget: any) => subtarget.index == params.subtargetIndex)._id);
  const actions = useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id == inEditingDocumentId).targets.find((target: any) => target.index == params.targetIndex).subtargets.find((subtarget: any) => subtarget.index == params.subtargetIndex).actions) || [];

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [formValues, setFormValues] = useState<any>({});
  const [formMode, setFormMode] = useState<TemplateModalMode>(TemplateModalMode.EDIT);
  const { register, trigger, handleSubmit, watch } = useForm({
    defaultValues: {
      title: "",
      subtitle: "",
      description: "",
    },
    values: formValues,
  });
  const toast = useToast();

  const { isOpen: isOpenOrder, onOpen: onOpenOrder, onClose: onCloseReorder } = useDisclosure()
  const { handleSubmit: handleSubmitReorder } = useForm({
  });
  const documentInEditingId = useSelector((state: any) => state.documents.inEditingDocumentId);


  const columns = [
    columnHelper.accessor("index", {
      cell: (info: any) => truncateString(info.getValue()),
      header: `Indice`,
    }),
    columnHelper.accessor("title", {
      cell: (info: any) => truncateString(info.getValue(), 80),
      header: `Titolo`,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          <IconButton
            colorScheme="yellow"
            aria-label="Edit"
            size="sm"
            onClick={() => { showModal(row.original) }}
            icon={<EditIcon />}
          />
          <IconButton
            colorScheme="red"
            aria-label="Delete"
            size="sm"
            onClick={() => { showModal(row.original, TemplateModalMode.DELETE) }}
            icon={<DeleteIcon />} />
          <Button
            colorScheme="blue"
            aria-label="Go to indicators"
            size="sm"
            as={Link}
            to={`${row.original.index}/indicators`}
          >
            Indicatori
            <ChevronRightIcon />
          </Button>
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];


  const onSubmit: SubmitHandler<any> = (data) => {

    const actionId = data._id;
    const actionTitle = data.title;
    const actionSubtitle = data.subtitle;
    const actionDescription = data.description;
    const actionIndex = data.index;
    const id = Number(actionIndex.split('.').pop());

    if (formMode == TemplateModalMode.EDIT) {

      patchDocumentAction(inEditingDocumentId, targetId, subtargetId, actionId, { title: actionTitle, subtitle: actionSubtitle, description: actionDescription, index: actionIndex, id: id })
        .then((res) => {
          dispatch(updateInEditingDocumentAction({
            targetIndex: params.targetIndex,
            subtargetIndex: params.subtargetIndex,
            actionIndex: data.index,
            actionId: actionId,
            actionTitle: actionTitle,
            actionSubtitle: actionSubtitle,
            actionDescription: actionDescription,
            id: id,
          }))

          toast({
            title: "Azione modificata con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
          })
        })
        .catch((error) => {

          toast({
            title: "Errore durante la modifica dell'azione",
            status: "error",
            duration: 6000,
            isClosable: true,
            description: error.status,

          })

        });
    } else if (formMode == TemplateModalMode.ADD) {
      postDocumentAction(inEditingDocumentId, targetId, subtargetId, { title: actionTitle, subtitle: actionSubtitle, description: actionDescription, index: actionIndex, id: id })
        .then((res) => {
          const newActionId = res._id;
          const newActionIndex = res.index;
          const newActionNumericalId = res.id;

          dispatch(addInEditingDocumentAction({
            _id: newActionId,
            targetIndex: params.targetIndex,
            subtargetIndex: params.subtargetIndex,
            actionIndex: newActionIndex,
            actionId: newActionId,
            actionTitle: actionTitle,
            actionSubtitle: actionSubtitle,
            actionDescription: actionDescription,
            id: newActionNumericalId,
          }))

          toast({
            title: "Azione aggiunta con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
          })
        })
        .catch((error) => {

          toast({
            title: "Errore durante l'aggiunta dell'azione",
            status: "error",
            duration: 6000,
            isClosable: true,
            description: error.status,
          })

        });

    } else if (formMode == TemplateModalMode.DELETE) {

      deleteDocumentAction(inEditingDocumentId, targetId, subtargetId, actionId)
        .then((res) => {
          dispatch(deleteInEditingDocumentAction({
            targetIndex: params.targetIndex,
            subtargetIndex: params.subtargetIndex,
            actionIndex: data.index,
          }))
          toast({
            title: "Azione eliminata con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
          })
        }).catch((error) => {
          toast({
            title: "Errore durante l'eliminazione dell'azione",
            status: "error",
            duration: 6000,
            isClosable: true,
            description: error.status,
          })
        })

    }
    onClose();
  }

  const showModal = (itemValues: any, mode: TemplateModalMode = TemplateModalMode.EDIT) => {
    if (mode == TemplateModalMode.EDIT) {
      setFormMode(TemplateModalMode.EDIT);
      setFormValues(itemValues);
    }
    else if (mode == TemplateModalMode.ADD) {
      setFormMode(TemplateModalMode.ADD);
      const newIndex = `${params.subtargetIndex}.${actions.length + 1}`;
      setFormValues({
        index: newIndex,
        title: "",
        subtitle: "",
        description: "",
      });
    } else if (mode == TemplateModalMode.DELETE) {
      setFormMode(TemplateModalMode.DELETE);
      setFormValues(itemValues);
    }
    onOpen();
  }

  const [newActions, setNewActions] = useState<any[]>(actions);
  const handlerOnChangeOrder = (newActions: any[]) => {
    setNewActions(newActions);
  }

  const onSubmitReorder: SubmitHandler<any> = (data) => {

    const actionsWithNewIndexes = newActions.map((action: any, index: number) => {
      return {
        ...action,
        index: updateIndexString(action.index, String(index + 1)),
      }
    });

    onCloseReorder();
    updateActionsReindex(
      documentInEditingId,
      params.targetIndex,
      params.subtargetIndex,
      params.actionIndex,
      actionsWithNewIndexes
    ).then((res) => {

      const reorderedActions = res.reorderedActions;

      dispatch(updateInEditingDocumentActions({
        targetIndex: params.targetIndex,
        subtargetIndex: params.subtargetIndex,
        actions: reorderedActions,
      }))

      toast({
        title: 'Riordinamento completato con successo.',
        description: "",
        status: 'success',
        duration: 6000,
        isClosable: true,
        position: "top"
      })

    }).catch((err) => {
      toast({
        title: 'Errore durante il riordinamento.',
        description: "",
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: "top"

      })
    })

  }

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Lista documenti", link: "/documents", value: "", isActive: true },
      { label: `Documento ${params.templateCode}`, link: `/documents/${params.templateCode}`, value: "", isActive: false },
      { label: `Obiettivi`, link: `/documents/${params.templateCode}/targets`, value: "", isActive: true },
      { label: `Obiettivo ${params.targetIndex}`, link: ``, value: "", isActive: false },
      { label: `Sotto-obiettivi`, link: `/documents/${params.templateCode}/targets/${params.targetIndex}/subtargets`, value: "", isActive: true },
      { label: `Sotto-obiettivo ${params.subtargetIndex}`, link: ``, value: "", isActive: false },
      { label: `Azioni`, link: ``, value: "", isActive: false },
    ]
  );


  return (
    <>
      <Modal isOpen={isOpenOrder} onClose={onCloseReorder} size={"2xl"}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmitReorder(onSubmitReorder)}>
            <ModalHeader>
              Riordina azioni
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <DndProvider backend={HTML5Backend}>
                <ContainerDnd cardss={actions} onChangeOrder={handlerOnChangeOrder} />
              </DndProvider>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onCloseReorder}>
                Chiudi
              </Button>
              <Button colorScheme="orange" mr={3} type="submit">
                Riordina
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose} size={"2xl"}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)} >
            <ModalHeader>
              Specifica azione {formValues.index}
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>

              {
                formMode == TemplateModalMode.ADD &&
                (
                  <FormControl mb={5}>
                    <FormLabel>Indice</FormLabel>
                    <Input placeholder="Inserisci l'indice" size='md' required={true}
                      isRequired={true}
                      isReadOnly={true}
                      {
                      ...register(`index`, { required: true })
                      }
                    />
                  </FormControl>
                )
              }

              <FormControl mb={5}>
                <FormLabel>Titolo</FormLabel>
                <Textarea placeholder='Inserisci il titolo' size='md' required={true}
                  isRequired={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  {
                  ...register(`title`, { required: true })
                  }
                />
              </FormControl>

              <FormControl mb={5}>
                <FormLabel>Sottotitolo</FormLabel>
                <Textarea placeholder='Inserisci il sottotitolo' size='md' required={true}
                  isRequired={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  {
                  ...register(`subtitle`, { required: true })
                  }
                />
              </FormControl>

              <FormControl mb={5}>
                <FormLabel>Descrizione</FormLabel>
                <Textarea placeholder='Inserisci la descrizione' size='md' required={true}
                  isRequired={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  {
                  ...register(`description`, { required: true })
                  }
                />
              </FormControl>

              <Input
                type={"hidden"}
                {
                ...register(`_id`, { required: false })
                }
              />

            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onClose}>
                Chiudi
              </Button>
              {formMode != TemplateModalMode.DELETE ?
                <Button colorScheme="green" mr={3} type="submit">
                  {
                    formMode == TemplateModalMode.EDIT ? "Salva modifiche" : "Aggiungi"
                  }
                </Button>
                :
                <Button colorScheme="red" mr={3} type="submit">
                  Elimina
                </Button>
              }
            </ModalFooter>

          </form>
        </ModalContent>
      </Modal >

      <section className="dashboard-first-section">
        <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
          <MyBreadcrumb breadcrumb={breadcrumb}/>


          <Card>
            <CardHeader>
              <Heading as="h1" size="xl" >
                Sotto-obiettivo {params.subtargetIndex} {">"} Azioni
              </Heading>
              <Flex mt={5}>
                <Link to={`/documents/${params.templateCode}/targets/${params.targetIndex}/subtargets`}>
                  <Button colorScheme="gray" alignContent={"start"} float={"right"} size={"xs"}>
                    <ChevronLeftIcon />
                    Torna ai sotto-obiettivi
                  </Button>
                </Link>
                <Spacer />
                <Button leftIcon={<DragHandleIcon />} colorScheme="orange" alignContent={"end"} float={"right"} disabled={true} onClick={onOpenOrder} size={"xs"} mr={1}>
                  Riordina azioni
                </Button>
                <Button leftIcon={<SmallAddIcon />} colorScheme="green" alignContent={"end"} float={"right"} size={"xs"} disabled={true} onClick={() => showModal({}, TemplateModalMode.ADD)} >
                  Aggiungi Azione
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <CustomTable data={actions} columns={columns} />
            </CardBody>
            <CardFooter>
            </CardFooter>
          </Card>
        </Container>
      </section>
    </>
  );
}